import type { CSSProperties } from 'vue'

import type { TableColumn } from './types'

export interface ColumnWidthDefaults {
  /** Cap for a numeric column. v3's `max-w-40`. */
  metric: number
  /** Cap for every other column. v3's `max-w-56`. */
  text: number
}

/**
 * v3 sizes a table by content and caps each cell (`OrphexTable.vue` has no `table-fixed`;
 * every `OrphexTd` carries `max-w-40`/`max-w-56`), so a thumbnail or an id column stays narrow
 * while a long name stops at the cap. A caller that declares a width on *every* column owns the
 * geometry instead, and that table is laid out fixed so the declared proportions survive being
 * stretched to fill its frame.
 */
export function usesDeclaredLayout<Row>(columns: readonly TableColumn<Row>[]): boolean {
  return columns.length > 0 && columns.every((column) => column.width !== undefined)
}

/**
 * A declared-geometry table states its total width so the surplus from `min-width: 100%` is shared
 * out in proportion to the declared columns; asking for `max-content` instead lets the browser
 * spread it its own way and the declared proportions drift.
 */
export function declaredTableWidth<Row>(
  columns: readonly TableColumn<Row>[],
  defaults: ColumnWidthDefaults,
): string | undefined {
  if (!usesDeclaredLayout(columns)) return undefined
  const total = columns.reduce(
    (sum, column) =>
      sum + (column.width ?? (column.numeric === true ? defaults.metric : defaults.text)),
    0,
  )
  return `max(${total}px, 100%)`
}

export function columnCellStyle<Row>(
  column: TableColumn<Row>,
  columns: readonly TableColumn<Row>[],
  isFrozen: (candidate: TableColumn<Row>) => boolean,
  defaults: ColumnWidthDefaults,
): CSSProperties {
  const widthOf = (candidate: TableColumn<Row>): number =>
    candidate.width ?? (candidate.numeric === true ? defaults.metric : defaults.text)
  let offset = 0
  for (const current of columns) {
    if (current.field === column.field) break
    if (isFrozen(current)) offset += widthOf(current)
  }
  // The per-kind cap is a default for a content-sized column. A column that declares its own
  // width owns it outright -- capping a declared 230px column at 224px silently shrinks it.
  const cap =
    column.maxWidth ??
    (column.width === undefined
      ? column.numeric === true
        ? defaults.metric
        : defaults.text
      : undefined)
  const style: CSSProperties = cap === undefined ? {} : { maxWidth: `${cap}px` }
  if (column.minWidth !== undefined) style.minWidth = `${column.minWidth}px`
  // A frozen column keeps a width so the offsets above can be summed from it.
  if (column.width !== undefined || (isFrozen(column) && column.flexible !== true)) {
    style.width = `${widthOf(column)}px`
  }
  if (isFrozen(column)) style.insetInlineStart = `${offset}px`
  return style
}
